/**
 * src/help/content.ts — the SHIPPED default help bundle (WO-H1).
 *
 * This is the offline-first source of truth: it is bundled into the binary so every
 * help surface renders with zero network. The backend may return per-tenant overrides
 * (GET /api/help-content) which HelpProvider merges OVER these defaults by id.
 *
 * Content rules (trade-agnostic, plain-language, no PII/secrets):
 *  - Write for a tradesperson, not a software user. Short sentences. Say what to tap.
 *  - Never name a trade or client here — keep it reusable across tenants.
 *  - Body is markdown-lite: blank-line paragraphs and `- ` bullets only.
 */
import type { HelpContentBundle } from '@beam/api-types';
import { SCREEN_KEYS, FLOW_KEYS } from './screenKeys';

export const DEFAULT_HELP_BUNDLE: HelpContentBundle = {
  version: 'h1-defaults-1',
  locale: 'en',

  entries: [
    {
      id: 'home',
      screenKey: SCREEN_KEYS.home,
      title: 'Your home screen',
      body:
        'This is your starting point. The tiles take you to the parts of the app you use most — estimates, receipts, invoices, and the review queue.\n\nTap any tile to open it. Tap the "?" on any screen if you get stuck.',
      relatedIds: ['help.center'],
    },
    {
      id: 'capture.empty',
      screenKey: SCREEN_KEYS.capture,
      stateKey: 'empty',
      title: 'Capturing a receipt',
      body:
        'Take a photo of a paper receipt and the app reads the details for you.\n\n- Tap the camera button and line the receipt up in the frame.\n- You can do this with no signal — it saves on your phone and uploads when you’re back online.\n- Nothing is filed until someone reviews it, so don’t worry about getting it perfect.',
      relatedIds: ['review', 'flow.capture-receipt'],
    },
    {
      id: 'capture',
      screenKey: SCREEN_KEYS.capture,
      title: 'Capturing a receipt',
      body:
        'Photograph a receipt and the app pulls out the amount, vendor, and date.\n\nAfter you capture it, the item waits in the Review Queue for someone to approve before it’s filed to a job.',
      relatedIds: ['review'],
    },
    {
      id: 'estimates',
      screenKey: SCREEN_KEYS.estimates,
      title: 'Estimates',
      body:
        'This is the list of every quote you’re working on.\n\n- Tap one to open it.\n- Tap "New Estimate" to start a fresh quote.\n- A price isn’t final until the owner approves it.',
      relatedIds: ['estimate.new', 'flow.build-estimate'],
    },
    {
      id: 'estimate.new',
      screenKey: SCREEN_KEYS.estimateNew,
      title: 'Building an estimate',
      body:
        'Add the work and materials for the job, and the app totals the price.\n\n- Add services and materials from your catalog so pricing stays consistent.\n- You can build and show a full estimate in the field.\n- Sending it for signature and setting binding pricing is the owner’s job — the app will guide you when you reach that step.',
      relatedIds: ['estimate.detail'],
    },
    {
      id: 'invoices',
      screenKey: SCREEN_KEYS.invoices,
      title: 'Invoices',
      body:
        'Every invoice you’ve created, with its status — draft, sent, paid, or overdue.\n\nTap an invoice to see it, send it, or take a payment.',
      relatedIds: ['invoice.detail', 'flow.send-invoice'],
    },
    {
      id: 'review',
      screenKey: SCREEN_KEYS.review,
      title: 'The review queue',
      body:
        'Captured receipts, texts, and voice notes wait here until a person checks them.\n\n- Open an item to see what the app read.\n- Fix anything that’s wrong, then approve it.\n- Approving files it to the right job and creates a contact if the sender is new.',
      relatedIds: ['capture'],
    },
    {
      id: 'help.center',
      screenKey: SCREEN_KEYS.helpCenter,
      title: 'Help Center',
      body:
        'Step-by-step guides for the things you do most. Search for a task, or pick a guide below.\n\nYou can also tap the "?" on any screen for help with just that screen.',
    },
    {
      id: 'estimate.detail',
      screenKey: SCREEN_KEYS.estimateDetail,
      title: 'Working an estimate',
      body:
        'This is the estimate itself — its lines, totals, and where it is in the process.\n\n- Add or edit services and materials while it’s a draft.\n- Approving it and sending for signature are owner actions; if a button is greyed out, the app tells you who can do it.',
      relatedIds: ['estimates'],
    },
    {
      id: 'invoice.detail',
      screenKey: SCREEN_KEYS.invoiceDetail,
      title: 'Working an invoice',
      body:
        'See the invoice, send it to the customer, and take a card payment.\n\nWhen the customer pays online, the status flips to “paid” on its own — no re-entry.',
      relatedIds: ['invoices'],
    },
    {
      id: 'queue',
      screenKey: SCREEN_KEYS.queue,
      title: 'Your queue',
      body:
        'Items waiting on you — captures and messages to review, and tasks to do next. Tap one to handle it.',
      relatedIds: ['review'],
    },
    {
      id: 'calendar',
      screenKey: SCREEN_KEYS.calendar,
      title: 'Calendar',
      body:
        'Your scheduled jobs and events. Tap a day to see what’s on, or tap an event for its details.',
    },
    {
      id: 'customers',
      screenKey: SCREEN_KEYS.customers,
      title: 'Customers',
      body:
        'Everyone you work for, with their jobs, estimates, and invoices in one place. Tap “+” to add a new customer.',
    },
    {
      id: 'voice',
      screenKey: SCREEN_KEYS.voice,
      title: 'Voice notes',
      body:
        'Talk instead of type. Record a note and the app turns your words into text for review — handy when your hands are full on site.',
    },
    {
      id: 'assistant',
      screenKey: SCREEN_KEYS.assistant,
      title: 'The assistant',
      body:
        'Ask in plain words and the assistant helps you find or draft things. It never changes anything without you approving it.',
    },
    {
      id: 'queue.empty',
      screenKey: SCREEN_KEYS.queue,
      stateKey: 'empty',
      title: 'All caught up',
      body:
        'Nothing needs you right now. New captures, messages, and tasks to handle will show up here.',
    },
    {
      id: 'review.empty',
      screenKey: SCREEN_KEYS.review,
      stateKey: 'empty',
      title: 'Nothing to review',
      body:
        'Captured receipts, texts, and voice notes land here for a quick check before they’re filed. Capture one to see it appear.',
    },
    {
      id: 'customers.empty',
      screenKey: SCREEN_KEYS.customers,
      stateKey: 'empty',
      title: 'No customers yet',
      body:
        'Add your first customer to start quoting and invoicing. Tap “New customer” to begin.',
    },
  ],

  coachmarks: [
    // Flow 1 — capture a receipt
    {
      id: 'cm.capture.1',
      flowKey: FLOW_KEYS.captureReceipt,
      stepOrder: 0,
      title: 'Snap a receipt',
      body: 'Tap the camera to photograph a paper receipt. The app reads the amount, vendor, and date for you.',
    },
    {
      id: 'cm.capture.2',
      flowKey: FLOW_KEYS.captureReceipt,
      stepOrder: 1,
      title: 'Works with no signal',
      body: 'Out in the field with no bars? It saves on your phone and uploads itself once you’re back online.',
    },
    {
      id: 'cm.capture.3',
      flowKey: FLOW_KEYS.captureReceipt,
      stepOrder: 2,
      title: 'Someone checks it',
      body: 'Your capture waits in the Review Queue for a quick check before it’s filed — so it’s fine if it isn’t perfect.',
    },
    // Flow 2 — build an estimate
    {
      id: 'cm.estimate.1',
      flowKey: FLOW_KEYS.buildEstimate,
      stepOrder: 0,
      title: 'Start a quote',
      body: 'Tap "New Estimate" and add the services and materials for the job from your catalog.',
    },
    {
      id: 'cm.estimate.2',
      flowKey: FLOW_KEYS.buildEstimate,
      stepOrder: 1,
      title: 'Show it on site',
      body: 'You can build and present a full-detail estimate to the customer right there in the field.',
    },
    {
      id: 'cm.estimate.3',
      flowKey: FLOW_KEYS.buildEstimate,
      stepOrder: 2,
      title: 'The owner sets the final price',
      body: 'Binding pricing and sending for signature are owner actions. The app shows you when to hand it off — you won’t get stuck.',
    },
    // Flow 3 — send an invoice
    {
      id: 'cm.invoice.1',
      flowKey: FLOW_KEYS.sendInvoice,
      stepOrder: 0,
      title: 'Open the invoice',
      body: 'Pick the invoice from the list to see its details and current status.',
    },
    {
      id: 'cm.invoice.2',
      flowKey: FLOW_KEYS.sendInvoice,
      stepOrder: 1,
      title: 'Send it',
      body: 'Send the invoice to the customer. They can pay online by card — the app never stores card numbers.',
    },
    {
      id: 'cm.invoice.3',
      flowKey: FLOW_KEYS.sendInvoice,
      stepOrder: 2,
      title: 'Watch it get paid',
      body: 'When the customer pays, the invoice flips to "paid" on its own. No re-entry.',
    },
  ],

  guides: [
    {
      id: 'guide.capture-receipt',
      title: 'How do I capture a receipt?',
      summary: 'Photograph a paper receipt and file it to a job.',
      steps: [
        { title: 'Open Capture', body: 'From Home, tap the Capture tile (or the camera button).', screenKey: SCREEN_KEYS.capture },
        { title: 'Take the photo', body: 'Line the whole receipt up in the frame and tap the shutter.', screenKey: SCREEN_KEYS.capture },
        { title: 'Let it upload', body: 'No signal is fine — it saves on your phone and uploads when you reconnect.' },
        { title: 'Approve it in Review', body: 'Open the Review Queue, check what the app read, fix anything, and approve.', screenKey: SCREEN_KEYS.review },
      ],
    },
    {
      id: 'guide.build-estimate',
      title: 'How do I build an estimate?',
      summary: 'Quote a job from your catalog and present it on site.',
      steps: [
        { title: 'Start a new estimate', body: 'From Estimates, tap "New Estimate".', screenKey: SCREEN_KEYS.estimateNew },
        { title: 'Add services and materials', body: 'Add line items from your catalog so the price stays consistent.', screenKey: SCREEN_KEYS.estimateNew },
        { title: 'Present it', body: 'Show the full-detail estimate to the customer right on your phone.', screenKey: SCREEN_KEYS.estimateDetail },
        { title: 'Hand off for approval', body: 'When the price needs to be final, the owner approves and sends it for signature.' },
      ],
    },
    {
      id: 'guide.send-invoice',
      title: 'How do I send an invoice and get paid?',
      summary: 'Send an invoice and let the customer pay online.',
      steps: [
        { title: 'Open the invoice', body: 'From Invoices, tap the one you want to send.', screenKey: SCREEN_KEYS.invoiceDetail },
        { title: 'Send it', body: 'Send it to the customer with a card-payment link.', screenKey: SCREEN_KEYS.invoiceDetail },
        { title: 'It marks itself paid', body: 'When the customer pays, the invoice updates to "paid" automatically.' },
      ],
    },
  ],
};
